import { useState, useRef, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { fetchCannedResponses, fetchCannedResponseById, selectCannedResponseOptions } from "../../../../store/slices/Tuition/manageCannedResponseSlice";
import lockIcon from '../../../../../public/assets/images/lockSolid.svg';
import { getTuitionAdminEmails } from "../../../../store/slices/Tuition/tuitionAdminSlice";
import api from "../../../../api/axios";
import endpoints from "../../../../api/endpoints";
import { detectEnvironmentFromUrl } from "../../../../utils/detectEnv";
import Notifications from "../../../../components/common/Notifications";
import { safeGet } from "../../../../utils/safeGet";
import { decodeHtmlEntities } from "../../../../utils/htmlUtils";

export const getEnvironmentFromAddress = (): string => {
  const env = detectEnvironmentFromUrl();

  if (env === 'prod') {
    return 'National-TRA@kp.org';
  }

  // For all lower environments (dev, qa, uat)
  return 'National-TRA-QA@kp.org';
};

interface TuitionEmailFormProps {
  selectedFilterValue: string;
  applicationInfo: Array<{
    applictnId: string | number;
    applRegTxnId: string | number;
    to: string[];
    cc: string[];
  }>;
  emailOptions?: {
    emails: Array<{
      id: number;
      code: string;
      value: string;
    }>;
  } | null;
  onCancel: () => void;
  onEmailSent?: () => void;
  applicationData?: {
    applicationId: string;
    employeeNuId?: string;
  };
}

const TuitionEmailForm = ({
  selectedFilterValue,
  applicationInfo,
  emailOptions,
  onCancel,
  onEmailSent,
  applicationData
}: TuitionEmailFormProps) => {
  const dispatch = useAppDispatch();
  const cannedResponseOptions = useAppSelector(selectCannedResponseOptions);

  // Environment-based email address
  const emailAddressFrom = getEnvironmentFromAddress();

  const [additionalCc, setAdditionalCc] = useState("");
  const [additionalCcError, setAdditionalCcError] = useState<string>("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string>("");
  const [subjectError, setSubjectError] = useState<string>("");
  const [messageError, setMessageError] = useState<string>("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch canned responses
  useEffect(() => {
    dispatch(fetchCannedResponses());
  }, [dispatch]);



  const handleCannedResponseClick = async (crId: number) => {
    try {
      const result = await dispatch(fetchCannedResponseById(crId)).unwrap();

      // Populate the form with the canned response
      setEmailSubject(decodeHtmlEntities(result.crSubject));
      setEmailMessage(decodeHtmlEntities(result.crBody));
    } catch (error) {
      console.error("Failed to fetch canned response:", error);
    }
  };

  const handleClearEmail = () => {
    setAdditionalCc("");
    setEmailSubject("");
    setEmailMessage("");
    setUploadedFiles([]);
    setFileError("");
    setSubjectError("");
    setMessageError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    // Clear any previous errors
    setFileError("");

    // Check for allowed file types FIRST
    const allowedMimeTypes = new Set([
      'image/jpeg',
      'image/png',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ]);

    const unsupportedFiles = files.filter(file => !allowedMimeTypes.has(file.type));
    if (unsupportedFiles.length > 0) {
      const fileNames = unsupportedFiles.map(file => `${file.name} (${file.type})`).join(', ');
      setFileError(`Unsupported file type(s): ${fileNames}. Allowed: JPEG, PNG, PDF, DOC, DOCX`);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Check if adding these files would exceed the limit
    if (uploadedFiles.length + files.length > 5) {
      setFileError("Maximum number of allowed files 5");
      return;
    }

    // Check for exe files
    const hasExeFile = files.some(file => file.name.toLowerCase().endsWith('.exe'));
    if (hasExeFile) {
      setFileError("Exe File is not allowed");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Check for file size limit (10MB = 10 * 1024 * 1024 bytes)
    const maxFileSize = 10 * 1024 * 1024;
    const oversizedFiles = files.filter(file => file.size > maxFileSize);
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(file => file.name).join(', ');
      setFileError(`File(s) exceed 10MB limit: ${fileNames}`);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // If all validations pass, add files
    setUploadedFiles(prev => [...prev, ...files]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setFileError("");
  };


  // Helper: Validate additional CC
  const validateAdditionalCc = (cc: string): boolean => {
    if (!cc?.trim()) return false;
    const emails = cc.split(',').map(email => email.trim()).filter(Boolean);
    const invalidCc = emails.some(email => !/^[^\s@]+@[^\s@]+\.(com|org)$/i.test(email));
    if (invalidCc) {
      setAdditionalCcError('Please enter valid email');
      return true;
    }
    return false;
  };

  // Helper: Refresh application info
  const refreshApplicationInfo = () => {
    for (const app of applicationInfo) {
      const appId = String(app.applictnId);
      const nuidFromEmail = app.to[0]?.split('@')[0];
      if (appId && nuidFromEmail) {
        dispatch(getTuitionAdminEmails({
          applicationId: appId,
          nuid: nuidFromEmail
        }));
      }
    }

    if (applicationData?.applicationId && applicationData?.employeeNuId) {
      const isAlreadyRefreshed = applicationInfo.some(app =>
        String(app.applictnId) === applicationData.applicationId
      );
      if (!isAlreadyRefreshed) {
        dispatch(getTuitionAdminEmails({
          applicationId: applicationData.applicationId,
          nuid: applicationData.employeeNuId
        }));
      }
    }
  };

  const handleSendEmail = async () => {
    // Clear previous validation errors
    setSubjectError("");
    setMessageError("");

    // Validate subject and message
    let hasValidationErrors = false;

    if (!emailSubject.trim()) {
      setSubjectError("Subject is required");
      hasValidationErrors = true;
    }

    if (!emailMessage.trim()) {
      setMessageError("Message is required");
      hasValidationErrors = true;
    }

    // Validate additional CC
    if (validateAdditionalCc(additionalCc)) {
      hasValidationErrors = true;
    }

    if (hasValidationErrors) {
      return;
    }

    if (applicationInfo.length === 0 || !emailAddressFrom) {
      setErrorMessage("Cannot send email: Application info and FROM fields are required");
      setShowErrorToast(true);
      setTimeout(() => setShowErrorToast(false), 15000);
      return;
    }

    setIsSendingEmail(true);

    try {
      // Create FormData for multipart/form-data request (for file attachments)
      const formData = new FormData();

      // Format date as MM-DD-YYYY
      const currentDate = new Date();
      const formattedDate = `${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}-${currentDate.getFullYear()}`;

      // Build additional CC array
      const additionalCcArray = additionalCc
        ? additionalCc.split(',').map(email => email.trim()).filter(email => email.length > 0)
        : [];

      // Build application info array with per-application to/cc emails
      // Only include cc field if it has values
      const applInfo = applicationInfo.map(app => {
        const result: {
          applId: string | number;
          applRegTxnId: string | number;
          to: string[];
          cc?: string[];
        } = {
          applId: app.applictnId,
          applRegTxnId: app.applRegTxnId,
          to: [...new Set([...app.to, emailAddressFrom])]
        };

        // Only add cc if there are cc emails
        if (app.cc && app.cc.length > 0) {
          result.cc = [...app.cc];
        }

        return result;
      });

      // Find emailType code
      const selectedEmailOption = emailOptions?.emails?.find(email => email.value === selectedFilterValue);
      const emailTypeCode = safeGet(selectedEmailOption?.code, safeGet(selectedFilterValue, 'admin'));

      // Add form data fields
      formData.append('from', emailAddressFrom);
      formData.append('applInfo', JSON.stringify(applInfo));
      formData.append('subject', emailSubject);
      formData.append('message', emailMessage);
      formData.append('date', formattedDate);
      formData.append('emailType', emailTypeCode);

      // Add additionalCc as a separate field if it has values
      if (additionalCcArray.length > 0) {
        formData.append('additionalCc', JSON.stringify(additionalCcArray));
      }

      // Add file attachments
      uploadedFiles.forEach((file) => {
        formData.append('files', file);
      });


      await api.post(endpoints.tuition.admin.sendEmail, formData);
      refreshApplicationInfo();

      // Show success notification
      setShowSuccessToast(true);

      // Wait 2 seconds before clearing form and closing
      setTimeout(() => {
        setShowSuccessToast(false);
        handleClearEmail();
        onEmailSent?.();
        onCancel();
      }, 2000);

    } catch (error: any) {
      const errMsg = safeGet(error.response?.data?.message, safeGet(error.message, 'Failed to send email'));
      setErrorMessage(`Error sending email: ${errMsg}`);
      setShowErrorToast(true);
      setTimeout(() => setShowErrorToast(false), 15000);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleCancel = () => {
    handleClearEmail();
    onCancel();
  };

  return (
    <div className="mt-8">
      {/* Success Notification */}
      {showSuccessToast && (
        <Notifications
          messageInfo="Email sent successfully!"
          status={true}
        />
      )}

      {/* Error Notification */}
      {showErrorToast && (
        <Notifications
          messageInfo={errorMessage}
          status={false}
        />
      )}

      <h2 className="h3 mb-6">Send an email</h2>

      {/* From and Date fields */}
      <div className="mb-3 flex flex-col gap-3">
        <div className="text-[14px]">
          <span className="font-semibold">From: </span>
          <span className="font-semibold">{emailAddressFrom}</span>
        </div>
        <div className="text-[14px]">
          <span className="font-semibold">Date: </span>
          <span className="font-semibold">{new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}</span>
        </div>
      </div>

      <div className="flex gap-[60px]">
        {/* Left Column - Email Fields */}
        <div className="flex flex-col gap-4" style={{ width: '100%' }}>
          {/* To Field */}
          <div className="ds-form">
            <label htmlFor="emailTo" className="label mb-2 block" style={{ marginTop: '12px' }}>
              To:
            </label>
            <div className="relative">
              <div className="input-with-icon">
                <input
                  autoComplete="on"
                  className="!w-full h-[48px] flex-shrink-0 block ds-form__control ds-form__disabled-field bg-gray-100 text-gray-600 pr-12"
                  disabled
                  id="emailTo"
                  type="text"
                  value={[...new Set([...applicationInfo.flatMap(app => app.to), emailAddressFrom])].join(", ")}
                  aria-describedby="emailTo-helper"
                  aria-invalid={false}
                />
                <button
                  type="button"
                  aria-label="To field is locked"
                  disabled
                  className="date-input-icon"
                >
                  <img src={lockIcon} alt="locked" />
                </button>
              </div>
            </div>
          </div>

          {/* Cc Field */}
          <div className="ds-form">
            <label htmlFor="emailCc" className="label mb-2 block">
              Cc:
            </label>
            <div className="relative">
              <div className="input-with-icon">
                <input
                  autoComplete="on"
                  className="!w-full h-[48px] flex-shrink-0 block ds-form__control ds-form__disabled-field bg-gray-100 text-gray-600 pr-12"
                  disabled
                  id="emailCc"
                  type="text"
                  value={[...new Set(applicationInfo.flatMap(app => app.cc))].join(", ")}
                  aria-describedby="emailCc-helper"
                  aria-invalid={false}
                />
                <button
                  type="button"
                  aria-label="Cc field is locked"
                  disabled
                  className="date-input-icon"
                >
                  <img src={lockIcon} alt="locked" />
                </button>
              </div>
            </div>
          </div>

          {/* Additional Cc Field */}
          <div className="ds-form">
            <label htmlFor="emailAdditionalCc" className="label mb-2 block">
              Additional Cc:
            </label>
            <div className="relative">
              <div className="input-with-icon">
                <input
                  autoComplete="on"
                  className="w-full block ds-form__control"
                  id="emailAdditionalCc"
                  type="text"
                  value={additionalCc}
                  onChange={(e) => {
                    const val = e.target.value;
                    setAdditionalCc(val);

                    // Clear CC error while user is editing (validate on Send click)
                    if (additionalCcError) {
                      setAdditionalCcError("");
                    }
                  }}
                  aria-describedby={additionalCcError ? 'emailAdditionalCc-error' : 'emailAdditionalCc-helper'}
                  aria-invalid={!!additionalCcError}
                />
              </div>
            </div>
            {additionalCcError && (
              <p id="emailAdditionalCc-error" className="text-[#D1480C] text-sm mt-1 flex items-center gap-2" role="alert">
                <img
                  src="/assets/images/errorCircleSmall.svg"
                  alt="Error icon"
                  className="!w-[16px] !h-[16px]"
                />
                {additionalCcError}
              </p>
            )}
          </div>

          {/* Subject Field */}
          <div className="ds-form">
            <label htmlFor="emailSubject" className="label mb-2 block">
              Subject:<span className="text-red-600">*</span>
            </label>
            <div className="relative">
              <input
                autoComplete="on"
                className={`w-full block ds-form__control ${subjectError ? 'border-red-600' : ''}`}
                id="emailSubject"
                type="text"
                value={emailSubject}
                onChange={(e) => {
                  setEmailSubject(e.target.value);
                  if (subjectError && e.target.value.trim()) {
                    setSubjectError("");
                  }
                }}
                style={{ width: '100%', fontSize: '16px' }}
                aria-describedby="emailSubject-counter emailSubject-error"
                aria-invalid={emailSubject.length > 250 || !!subjectError}
                maxLength={250}
              />
              {subjectError && (
                <p id="emailSubject-error" className="text-[#D1480C] text-sm mt-1 flex items-center gap-2" role="alert">
                  <img
                    src="/assets/images/errorCircleSmall.svg"
                    alt="Error icon"
                    className="!w-[16px] !h-[16px]"
                  />
                  {subjectError}
                </p>
              )}
              <div className="character-count mt-2">
                <span
                  id="emailSubject-counter"
                  aria-live="polite"
                  aria-atomic="true"
                  className={`text-sm ${emailSubject.length > 225 ? 'text-red-600' : 'text-gray-600'}`}
                >
                  {`${250 - emailSubject.length} of 250 characters left`}
                </span>
              </div>
            </div>
          </div>

          {/* Message Field */}
          <div className="ds-form">
            <label htmlFor="emailMessage" className="label mb-2 block">
              Message:<span className="text-red-600">*</span>
            </label>
            <div className="relative">
              <textarea
                className={`w-full block ds-form__control ${messageError ? 'border-red-600' : ''}`}
                id="emailMessage"
                value={emailMessage}
                onChange={(e) => {
                  setEmailMessage(e.target.value);
                  if (messageError && e.target.value.trim()) {
                    setMessageError("");
                  }
                }}
                style={{ width: '100%', borderRadius: '4px', padding: '8px', border: messageError ? '1px solid #dc2626' : '1px solid rgb(136, 144, 159)', fontSize: '16px' }}
                aria-describedby="emailMessage-counter emailMessage-error"
                aria-invalid={emailMessage.length > 4000 || !!messageError}
                rows={10}
                maxLength={4000}
              />
              {messageError && (
                <p id="emailMessage-error" className="text-[#D1480C] text-sm mt-1 flex items-center gap-2" role="alert">
                  <img
                    src="/assets/images/errorCircleSmall.svg"
                    alt="Error icon"
                    className="!w-[16px] !h-[16px]"
                  />
                  {messageError}
                </p>
              )}
              <div className="character-count mt-2">
                <span
                  id="emailMessage-counter"
                  aria-live="polite"
                  aria-atomic="true"
                  className={`text-sm ${emailMessage.length > 3600 ? 'text-red-600' : 'text-gray-600'}`}
                >
                  {`${4000 - emailMessage.length} of 4000 characters left`}
                </span>
              </div>
            </div>
          </div>

          {/* Attachment Section */}
          <div className="ds-form">
            <h3 className="h3 mb-4">Attachment</h3>

            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
              multiple
            />

            <div
              style={{
                display: 'flex',
                height: '97px',
                width: '557px',
                padding: '16px',
                alignItems: 'center',
                gap: '24px',
                border: '1px solid var(--Slate, #878F9B)',
                background: 'var(--Pebble, #F7F7F7)'
              }}
            >
              <button
                type="button"
                className="button -primary"
                onClick={handleChooseFile}
                disabled={uploadedFiles.length >= 5}
              >
                <img
                  src="/assets/images/uploadOutline.svg"
                  alt="upload icon"
                  className="!w-[1.6rem] !h-[1.25rem] inline-block align-middle mr-2"
                  aria-hidden="true"
                /> Choose File
              </button>

              <ul className="list-disc pl-5 text-sm">
                <li className="text-gray-700">Exe File is not allowed</li>
                <li className="text-gray-700">Maximum number of allowed files 5</li>
                <li className="text-gray-700">Maximum file size 10MB</li>
              </ul>
            </div>

            {fileError && (
              <p className="text-[#D1480C] text-sm mt-2 flex items-center gap-2">
                <img
                  src="/assets/images/errorCircleSmall.svg"
                  alt="Error icon"
                  className="!w-[16px] !h-[16px]"
                />
                {fileError}
              </p>
            )}

            {/* Display uploaded files */}
            {uploadedFiles.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">Selected files ({uploadedFiles.length}/5):</p>
                <ul className="space-y-2">
                  {uploadedFiles.map((file, index) => (
                    <li key={`${file.name}-${file.size}-${file.lastModified}`} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <span className="text-sm truncate flex-1">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        className="text-red-600 hover:text-red-800 ml-2 text-sm"
                        style={{ color: 'rgb(0, 116, 173)', cursor: 'pointer' }}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-4">
            <button className="button -secondary" onClick={handleClearEmail} disabled={isSendingEmail}>
              Clear
            </button>
            <button className="button -secondary" onClick={handleCancel} disabled={isSendingEmail}>
              Cancel
            </button>
            <button
              className="button -primary -light"
              onClick={handleSendEmail}
              disabled={isSendingEmail || applicationInfo.length === 0 || !emailAddressFrom || emailMessage.length > 4000 || emailSubject.length > 250 || !!additionalCcError}
            >
              {isSendingEmail ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>

        {/* Right Column - Canned Responses */}
        <div style={{ width: '530px', background: 'var(--color-blue-5, #F2F8FB)', padding: '24px', height: 'fit-content' }}>
          <h3 className="h3 mb-4">Available Canned responses</h3>
          <ul className="list-disc pl-5 space-y-2">
            {cannedResponseOptions.map((option) => (
              <li key={option.crId} className="cursor-pointer w-fit">
                <button
                  type="button"
                  onClick={() => handleCannedResponseClick(option.crId)}
                  className="bg-transparent border-0 p-0 cursor-pointer hover:underline"
                >
                  <b className="text-[#0074AD]">{option.crName}</b>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TuitionEmailForm;